import { DIGITAL_PRODUCTS_FREE_LISTING_TYPE } from '../config/configListing';
import { displayPrice } from './configHelpers';
import { formatMoney } from './currency';
import { ensureListing } from './data';
import { isContentSubscriptionListing } from './digitalSubscription';
import { getPriceTiersFromListing, isTierEligible } from './priceTiers';
import { types as sdkTypes } from './sdkLoader';
import { isFreeGroupSession } from './soulwise-marketplace/listingType';

const { Money } = sdkTypes;

const availablePriceTiers = (listing, tierContext = {}) => {
  const catalog = getPriceTiersFromListing(listing);
  if (!catalog) {
    return [];
  }
  const ctx = {
    now: tierContext.now || new Date(),
    listingType: listing?.attributes?.publicData?.listingType,
    publishedAt: tierContext.publishedAt || listing?.attributes?.createdAt || null,
    bookingStart: tierContext.bookingStart,
    unitsSoldByTierId: tierContext.unitsSoldByTierId || {},
  };
  return catalog.items.filter(tier => isTierEligible(tier, ctx));
};

/**
 * Free group sessions are stored as Money(1, 'USD') so checkout still works.
 * Display must never show that dummy cent — use the Free label instead.
 */
export const isFreeListingDisplay = listing => {
  const listingType = listing?.attributes?.publicData?.listingType;
  return isFreeGroupSession(listingType) || listingType === DIGITAL_PRODUCTS_FREE_LISTING_TYPE;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

export const getPriceFromListing = (listing, config, intl, tierContext) => {
  const currentListing = ensureListing(listing);

  const {
    attributes: { price, publicData },
  } = currentListing;
  const { listingType } = publicData || {};
  const freeLabel = intl.formatMessage({ id: 'OrderPanel.listingPriceFree' });

  if (isFreeListingDisplay(currentListing)) {
    return {
      formattedPrice: freeLabel,
      priceTitle: freeLabel,
      unitType: publicData?.unitType,
    };
  }

  const validListingTypes = config?.listing?.listingTypes;
  const foundListingTypeConfig = validListingTypes?.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  const usePriceTiers = !isContentSubscriptionListing(currentListing);
  const catalog = usePriceTiers ? getPriceTiersFromListing(currentListing) : null;
  const available = catalog ? availablePriceTiers(currentListing, tierContext) : [];

  // Never advertise a From price for ineligible / sold-out tiers.
  if (catalog && available.length === 0) {
    return {
      formattedPrice: null,
      priceTitle: null,
      unitType: publicData?.unitType,
      noPriceOptions: true,
    };
  }

  const selectedAvailable = tierContext?.selectedTierId
    ? available.find(tier => tier.id === tierContext.selectedTierId)
    : null;
  const lowestAvailable =
    available.length > 0
      ? available.reduce((min, tier) => (tier.amount < min.amount ? tier : min))
      : null;
  const displayTier = selectedAvailable || (available.length === 1 ? available[0] : null);
  const displayPriceMoney = displayTier
    ? new Money(displayTier.amount, displayTier.currency)
    : lowestAvailable
    ? new Money(lowestAvailable.amount, lowestAvailable.currency)
    : price;
  const { formattedPrice, priceTitle } = priceData(displayPriceMoney, config?.currency, intl);
  const showFrom = !selectedAvailable && !displayTier && available.length >= 2;
  const fromLabel =
    showFrom && lowestAvailable && formattedPrice
      ? intl.formatMessage({ id: 'OrderPanel.priceFrom' }, { price: formattedPrice })
      : formattedPrice;
  return {
    formattedPrice: showPrice ? fromLabel || freeLabel : freeLabel,
    priceTitle: showFrom ? fromLabel : priceTitle,
    unitType: publicData?.unitType,
    noPriceOptions: false,
  };
};
